import {AstView, EnrichedAstNode} from '../../types';
import {visitor} from '../../helpers';
import {isNodeInsideBox, isNodeVisualInsideBox} from '../../helpers/GeometryHelpers';
import {remove} from 'lodash';

/**
 Adjust nodes to be put inside parent geometric parent.
```
   ┌───────────┐
   │ ┌─┐       │
   │ │B│       │
   │ └─┘       │
   │       A   │
   └───────────┘
```
##### Before
```
Root
    A
    B
```
##### After
```
 Root
    A
        B
```
 */
export function findBetterParent(tree: EnrichedAstNode, view: AstView) {
    const {meta} = view;

    visitor(tree, meta, (currentNode, meta, parent) => {
        // see if children overlap. If they do, and the parent is of frame/rect/group type, we can add element as child.

        if (currentNode.children.length < 2) {
            return;
        }

        if (currentNode.type === 'Text') {
            return;
        }

        if (parent && parent.type === 'Text') {
            return;
        }

        if (parent?.userInput.lockChildren) {
            return;
        }

        // const possibleParents = currentNode.children.filter(isPossibleParent);

        // if (possibleParents.length === 0) {
        //     return;
        // }

        //        const sortedList = [...currentNode.children].sort((a, b) => b.zIndex - a.zIndex);

        // sortedList.forEach((child) => {
        //     const parents = possibleParents.filter((possibleParent) =>
        //         isGeometricPossibleParent(child, possibleParent, sortedList)
        //     );

        //     if (parents.length === 0) {
        //         return;
        //     }
        //     const sortedParents = parents.sort((a, b) => b.zIndex - a.zIndex);
        //     const newParent = sortedParents[0];
        //     remove(currentNode.children, (node) => node === child);
        //     newParent.children.push(child);
        // });

        const sortedNodes = [...currentNode.children].sort((a, b) => a.zIndex - b.zIndex);

        while (sortedNodes.length > 0) {
            const possibleParent = sortedNodes.shift();

            if (!possibleParent) {
                break;
            }

            if (!isPossibleParent(possibleParent)) {
                continue;
            }

            // is possible parent
            [...sortedNodes].forEach((possibleChild) => {
                if (isGeometricPossibleParent(possibleChild, possibleParent, currentNode.children)) {
                    remove(currentNode.children, (node) => node === possibleChild);
                    remove(sortedNodes, (node) => node === possibleChild);
                    possibleParent.children.push(possibleChild);
                    possibleParent.children = possibleParent.children.sort((a, b) => a.zIndex - b.zIndex);
                }
            });
        }
    });
}

const isGeometricPossibleParent = (
    nodeToGroup: EnrichedAstNode,
    possibleParent: EnrichedAstNode,
    siblings: readonly EnrichedAstNode[]
) => {
    const safeSiblings = siblings.filter(
        (sib) => sib.internalId !== nodeToGroup.internalId && sib.internalId !== possibleParent.internalId
    );

    if (possibleParent.internalId === nodeToGroup.internalId) {
        return false;
    }

    // if not inside parent. can't group.
    if (!isNodeVisualInsideBox(nodeToGroup, possibleParent.position).inside) {
        return false;
    }

    // if parent is in front of child can not group
    if (possibleParent.zIndex > nodeToGroup.zIndex) {
        return false;
    }

    // children possibly caught in the middle of this move.
    const middleChildren = safeSiblings.filter(
        (sib) => possibleParent.zIndex < sib.zIndex && sib.zIndex < nodeToGroup.zIndex
    );

    // check for overlap of child and middleChildren
    const hasClearPath = middleChildren.every((middle) => {
        const result = isNodeInsideBox(middle, nodeToGroup.position);
        return !result.overlap;
    });

    if (!hasClearPath) {
        return false;
    }

    return true;
};

const isPossibleParent = (node: EnrichedAstNode) => {
    if (!['Frame', 'Image'].includes(node.type)) {
        return false;
    }

    if (node.userInput.lockChildren) {
        return false;
    }

    if (node.inputLayoutAbstraction.absolutePosition) {
        return false;
    }

    if (node.styles['filter']) {
        return false;
    }
    return true;
};
